import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

const generateGameManifest = httpsCallable(functions, 'generateGameManifest');

/**
 * Concept → LLM graphical asset manifest entry point for Game Studio.
 * @param {{
 *   selectedProject?: { id?: string } | null,
 *   onNavigateToQueue?: () => void,
 *   onSuccessToast?: (message: string) => void,
 *   onErrorToast?: (message: string) => void,
 * }} [props]
 */
export default function AssetGenerator({
  selectedProject = null,
  onNavigateToQueue,
  onSuccessToast,
  onErrorToast,
}) {
  const [conceptText, setConceptText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [localError, setLocalError] = useState('');

  const projectId =
    typeof selectedProject?.id === 'string' ? selectedProject.id.trim() : '';
  const canGenerate =
    Boolean(projectId) && conceptText.trim().length > 0 && !isGenerating;

  const handleGenerate = async () => {
    const trimmed = conceptText.trim();
    if (!projectId) {
      const msg = 'Save or select a game project before generating a manifest.';
      setLocalError(msg);
      onErrorToast?.(msg);
      return;
    }
    if (!trimmed) {
      const msg = 'Enter a game concept and art direction first.';
      setLocalError(msg);
      onErrorToast?.(msg);
      return;
    }

    setLocalError('');
    setIsGenerating(true);
    try {
      const result = await generateGameManifest({
        conceptText: trimmed,
        projectId,
        gameId: projectId,
      });
      const data = result?.data || {};
      const assetCount = Number(data.assetCount) || 0;
      const title =
        typeof data.gameTitle === 'string' && data.gameTitle.trim()
          ? data.gameTitle.trim()
          : 'Game';

      setConceptText('');
      const successMsg =
        assetCount > 0
          ? `${title}: planned ${assetCount} draft asset${assetCount === 1 ? '' : 's'}. Review them in the Render Queue before queuing generation.`
          : `${title}: manifest created. Open Render Queue to review draft assets.`;
      onSuccessToast?.(successMsg);
      onNavigateToQueue?.();
    } catch (err) {
      const msg =
        err?.message ||
        err?.details ||
        'Could not generate the game manifest.';
      setLocalError(typeof msg === 'string' ? msg : String(msg));
      onErrorToast?.(typeof msg === 'string' ? msg : String(msg));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 960 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            AI Manifest Generator
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Describe your game concept and art direction. Gemini will produce a
            complete graphical asset manifest (protagonist animations, enemies,
            backgrounds, and UI). New assets are saved as drafts so Stability
            does not start until you queue them.
          </Typography>
        </Box>

        {!projectId ? (
          <Alert severity="info">
            Select or save a game project to generate a manifest for it.
          </Alert>
        ) : null}

        {localError ? (
          <Alert severity="error" onClose={() => setLocalError('')}>
            {localError}
          </Alert>
        ) : null}

        {isGenerating ? (
          <Box>
            <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Generating manifest with Gemini — this can take a moment…
            </Typography>
          </Box>
        ) : null}

        <TextField
          label="Game Concept & Art Direction"
          value={conceptText}
          onChange={(e) => setConceptText(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          disabled={isGenerating || !projectId}
          placeholder={
            'Describe your game concept (e.g., 2D zombie shooter, metal slug style, 3 levels...)'
          }
          helperText="Be specific about style, palette, characters, and required screens."
        />

        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerate}
            disabled={!canGenerate}
            loading={isGenerating}
            aria-busy={isGenerating}
            startIcon={
              isGenerating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeOutlinedIcon />
              )
            }
            sx={{ textTransform: 'none' }}
          >
            {isGenerating ? 'Generating Manifest…' : 'Generate Game Manifest'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
